
import React, { useState, useEffect,useMemo, useCallback ,useRef } from "react";
import axios from "axios";import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Pagination,
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { BsFillCircleFill } from "react-icons/bs"; // 🟢 unread‐indicator icon

/* -------------------------- helper functions -------------------------- */
function minutesAgo(dateString) {
  const diffSec = Math.floor((Date.now() - new Date(dateString)) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
}

function labelForStatus(code) {
  switch (code) {
    case 1:
      return "New";
    case 2:
      return "In progress";
    case 3:
      return "Accepted";
    case 4:
      return "Completed";
    default:
      return "Unknown";
  }
}

const STATUS_COLORS = {
  New: "primary",
  "In progress": "warning",
  Accepted: "success",
  Completed: "success",
  Unknown: "default",
};

/* ------------------------------- main --------------------------------- */
export default function OrderProcess() {
   /* ------------ pagination + batching configs ------------ */
 
  const batchSize = 100; // one backend fetch = 30 records
 const [quotes, setQuotes] = useState([]);
 
  const [totalRecords, setTotalRecords] = useState(0);


  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();
 const [rowsPerPage, setRowsPerPage] = useState(6);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const [sortDescriptor, setSortDescriptor] = useState({
     column: null,
     direction: null, // "ascending" | "descending"
   });


     /* ------------------- refs for batching ------------------- */
  const batchPageRef = useRef(0);
  const fetchingRef = useRef(false);
  const allDataLoadedRef = useRef(false);

  /* ---------- NEW: accept quote then open detail page ---------- */
  const acceptQuoteAndOpen = useCallback(
    async (quoteId) => {
      try {
        await axios.put(
          `${process.env.REACT_APP_SERVER_URL}/quotes/acceptquote/${quoteId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error("PUT /acceptquote failed → still opening page", err);
      } finally {
        navigate(`/quotes/${quoteId}`);
      }
    },
    [navigate, token]
  );

  /* --------------------------- data fetch ----------------------------- */
  // const fetchData = useCallback(async () => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const { data } = await axios.get(
  //       `${process.env.REACT_APP_SERVER_URL}/quotes/acceptedquote`,
  //       {
  //         params: { pageSize, page },
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );
  //     setRows(data?.data ?? []);
  //     setTotalPages(data?.index?.totalPages ?? 1);
  //   } catch (err) {
  //     setError(
  //       err.response?.data?.message || err.message || "Failed to load data"
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [page, token]);
  /* ----------------------- batch fetch ---------------------- */
const fetchBatch = useCallback(
  async (batchNum, { replace = false } = {}) => {
    if (fetchingRef.current) return;

    try {
      fetchingRef.current = true;
      if (replace) {
        setLoading(true); // Only show global loader when replacing all data
      }

      const qp = new URLSearchParams();
      qp.append("page", batchNum);
      qp.append("pageSize", batchSize);

      if (sortDescriptor.column) {
        qp.append("sortBy", sortDescriptor.column);
        qp.append("sortOrder", sortDescriptor.direction === "ascending" ? "asc" : "desc");
      }

      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/quotes/acceptedquote?${qp}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fetchedRows = res.data?.data || [];
      const backendIndex = res.data?.index || {};
      const backendTotalRecords = backendIndex.totalRecords || 0;

      // ✅ update total counts
      setTotalRecords(backendTotalRecords);
      setTotalPages(Math.ceil(backendTotalRecords / rowsPerPage));

      setQuotes((prev) => {
        if (replace || batchNum === 1) return fetchedRows;
        const prevIds = new Set(prev.map((r) => r.id));
        const uniqueNew = fetchedRows.filter((r) => !prevIds.has(r.id));
        return [...prev, ...uniqueNew];
      });

      batchPageRef.current = batchNum;

      // ✅ only mark as allDataLoaded when backendTotalRecords <= quotes.length
      if ((batchNum * batchSize) >= backendTotalRecords) {
        allDataLoadedRef.current = true;
      } else {
        allDataLoadedRef.current = false;
      }

    } catch (err) {
      console.error("fetchBatch error:", err.response?.data || err.message || err);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  },
  [token, sortDescriptor, batchSize, rowsPerPage]
);


  /* ------------- ensure data available for page ------------- */
  const ensurePageData = useCallback(
    async (uiPage) => {
      const requiredEndIndex = uiPage * rowsPerPage;
      if (requiredEndIndex <= quotes.length) return;
      if (allDataLoadedRef.current) return;

      const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
      let startBatch = batchPageRef.current || 0;

      for (let b = startBatch + 1; b <= requiredBatch; b++) {
        await fetchBatch(b);
        if (allDataLoadedRef.current) break;
      }
    },
    [quotes.length, rowsPerPage, batchSize, fetchBatch]
  );

  useEffect(() => {
    fetchBatch(1, { replace: true });
  }, [fetchBatch]);

const headerColumns = [
  { uid: "status", name: "Status" },
  { uid: "industry", name: "Industry" },
  { uid: "car", name: "Car" },
  { uid: "part", name: "Part" },
  { uid: "created", name: "Created" },
];
 useEffect(() => {
    ensurePageData(page);
  }, [page, ensurePageData]);


 /* -------------------- sorting helper -------------------- */
const getSortValue = (item, column) => {
  const req = item.Request || {};       // <- directly from item
  const user = req.User || {};
 
  switch (column) {
    case "status":
      return item.QuoteStatus?.status?.toLowerCase() || "";
    case "customer":
      return `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
    case "car":
      const make = req.VehicleMake?.name || req.custom_make || "";
      const model = req.VehicleModel?.name || req.custom_model || "";
      return `${make} ${model}`.toLowerCase();
    case "part":
      return req.Part?.name || req.custom_part_type || "";
    case "created":
      return new Date(item.createdAt || req.date_requested).getTime() || 0;
    default:
      return "";
  }
};


 // 1. Sorting logic
 const sortedItems = useMemo(() => {
  if (!sortDescriptor.column) return quotes; // ← changed from filteredItems
  return [...quotes].sort((a, b) => {
    let v1 = getSortValue(a, sortDescriptor.column);
    let v2 = getSortValue(b, sortDescriptor.column);

    // Number comparison
    if (!isNaN(v1) && !isNaN(v2)) {
      return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;
    }

    // String comparison
    v1 = v1.toString().toLowerCase();
    v2 = v2.toString().toLowerCase();
    return sortDescriptor.direction === "ascending"
      ? v1.localeCompare(v2)
      : v2.localeCompare(v1);
  });
}, [quotes, sortDescriptor]); // ← dependency changed

// 2. Pagination applied AFTER sorting
const pagedItems = useMemo(() => {
   const start = (page - 1) * rowsPerPage; // ← c
  return sortedItems.slice(start, start + rowsPerPage);
}, [sortedItems, page, rowsPerPage]);


  /* ---------------------- loading / error UI -------------------------- */
 if (loading && quotes.length === 0)
    return (
      <div className="flex justify-center items-center h-48">
        <Spinner size="lg" />
      </div>
    );
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  /* ----------------------------- table -------------------------------- */
  return (
    <Table
      aria-label="Accepted Quotes"
      isStriped
      isHeaderSticky
       sortDescriptor={sortDescriptor}
         onSortChange={setSortDescriptor}
      bottomContent={
        totalPages > 1 && (
          <div className="py-2 flex justify-center">
            <Pagination
              showControls
              color="primary"
              page={page}
              total={totalPages}
              onChange={(newPage) => {
              setPage(newPage);
              ensurePageData(newPage);
                }}
            />
          </div>
        )
      }
      bottomContentPlacement="outside"
      classNames={{
        thead: "bg-gray-100",
        th: "text-left text-sm font-medium text-gray-700 px-4 py-2",
        td: "text-sm text-gray-800 px-4 py-3 whitespace-nowrap",
      }}
    >
    <TableHeader columns={headerColumns}>
  {(column) => (
    <TableColumn
      key={column.uid}
      allowsSorting
      onSortChange={(direction) => {
        setSortDescriptor({ column: column.uid, direction });
      }}
    >
      {column.name}
    </TableColumn>
  )}
</TableHeader>

      <TableBody
        emptyContent={
          <div className="py-10 text-center text-gray-500 text-sm">
            No Data Found
          </div>
        }
      >
        {pagedItems.map((q) => {
          const req = q.request || q.Request || {};
          const user = req.User || {};
          const statusLabel = labelForStatus(q.status);
          const statusColor = STATUS_COLORS[statusLabel];

          const industry =
            user.business_name || user.Industry || "Not Provided";
          const make =
            req.VehicleMake?.name || req.custom_make || "Not Provided";
          const model =
            req.VehicleModel?.name || req.custom_model || "Not Provided";
          const carLabel = `${make} ${model}`.trim();
          const part = req.custom_part_type || req.Part?.name || "Not Provided";

          return (
            <TableRow
              key={q.id}
              onClick={() => acceptQuoteAndOpen(q.id)} // ← trigger PUT then navigate
              className="cursor-pointer"
            >
              {/* Status + unread indicator */}
              <TableCell>
                <div className="flex items-center gap-2">
                  <Chip
                    variant="flat"
                    color={statusColor}
                    className="capitalize"
                  >
                    {statusLabel}
                  </Chip>
                  {!q.is_viewed && (
                    <BsFillCircleFill size={10} className="text-green-600" />
                  )}
                </div>
              </TableCell>

              <TableCell>{industry}</TableCell>
              <TableCell>{carLabel || "-"}</TableCell>
              <TableCell className="capitalize">{part}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {minutesAgo(q.createdAt)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(q.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
